import { toStateISO } from "utils/regions";
import { compressImgFileIfLargerThanTarget } from "utils";

export const SUPPORTING_DOCUMENTS_FILETYPES = "image/png,image/jpeg,.pdf";
export const DOCUMENTS_FILETYPES = "image/png,image/jpeg";

/**
 * Convert an array of File objects to base64 encoded strings
 * @param {File[]} fileList
 * @returns {Promise} Array of base64 encoded strings
 */
async function fileListToBase64(fileList) {
  function getBase64(file) {
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onload = (ev) => {
        resolve(ev.target.result);
      };
      reader.readAsDataURL(file);
    });
  }

  async function compressImgIfNeededAndReturnBase64(file) {
    const optImg = await compressImgFileIfLargerThanTarget(file);
    const b64 = await getBase64(optImg);
    return b64;
  }

  const promises = [];
  for (let i = 0; i < fileList.length; i++) {
    const f = fileList[i];
    const p = f.type.includes("image/") ? compressImgIfNeededAndReturnBase64(f) : getBase64(f);
    promises.push(p);
  }

  return await Promise.all(promises);
}

/**
 * Convert the file objects for documents (drivers license, passport, etc)
 * into base64 encoded strings to send over the wire
 */
async function prepDocuments(document_front, document_back) {
  let base64EncodedFiles = [];

  if (document_front) {
    const fileList = [document_front];
    if (document_back) {
      fileList.push(document_back);
    }
    base64EncodedFiles = await fileListToBase64(fileList);
  }

  return base64EncodedFiles;
}

/**
 * Extra files object structure
 * @typedef {Object} ExtraFile
 * @prop {String} file_name
 * @prop {String} file_ext
 * @prop {String} file_mime_type
 * @prop {String} file_contents
 */

/**
 * Convert the File objects for supporting documents (utility bills, bank
 * statements, etc) into an array of {ExtraFile} objects to send over the wire
 */
async function prepSupportingDocuments(extra_files) {
  const base64Files = await fileListToBase64(extra_files);
  const strippedBase64Files = base64Files.map((file) => file.split(",")[1]);
  return extra_files.map((file, index) => ({
    file_name: file.name.substring(0, file.name.lastIndexOf(".")),
    file_ext: `.${file.name.split(".").pop()}`,
    file_mime_type: file.type,
    file_contents: strippedBase64Files[index],
  }));
}

export const formatFormDataForTaxUpdate = async (formData) => {
  const { personal, contact, taxInfo } = formData;
  const mailingAddress = contact.mailingAddress.country ? contact.mailingAddress : contact;
  let firstName = personal.first;
  if (personal.middle) {
    firstName += ` ${personal.middle}`;
  }
  return {
    name: {
      first: firstName,
      last: personal.last,
      metadata: {
        source: "kyc",
        collection_method: "tilia_widget_entry",
      },
    },
    residential_address: {
      street1: contact.street,
      street2: contact.street2,
      city: contact.city,
      state: contact.country === "US" ? toStateISO(contact.country, contact.state) : contact.state,
      postal_code: contact.zip,
      country: contact.country,
      metadata: {
        source: "kyc",
        collection_method: "tilia_widget_entry",
      },
    },
    mailing_address: {
      street1: mailingAddress.street,
      street2: mailingAddress.street2,
      city: mailingAddress.city,
      state:
        mailingAddress.country === "US"
          ? toStateISO(mailingAddress.country, mailingAddress.state)
          : mailingAddress.state,
      postal_code: mailingAddress.zip,
      country: mailingAddress.country,
      metadata: {
        source: "kyc",
        collection_method: "tilia_widget_entry",
      },
    },
    tax_info: {
      signature: taxInfo.signature,
    },
  };
};

/**
 * Format form data for API
 */
export const formatFormDataForApi = async (formData) => {
  let {
    personal: { dob, ...personal },
    taxInfo,
    contact: {
      useForMailingAddress,
      mailingAddress: {
        state: mailingState,
        street: mailingStreet,
        street2: mailingStreet2,
        city: mailingCity,
        zip: mailingZip,
        country: mailingCountry,
      },
      state,
      street,
      street2,
      city,
      zip,
      country,
    },
    documents: {
      document_type,
      document_front,
      document_back,
      is_address_on_document,
      extra_files,
      ...documents
    },
    __metadata: { permanentGooglePlaceFormattedAddress, mailingGooglePlaceFormattedAddress },
    pii_level,
  } = formData;

  const permanentAddress = {
    state,
    street,
    street2,
    city,
    zip,
    country,
    google_place_formatted_address: permanentGooglePlaceFormattedAddress,
  };

  const mailingAddress = {
    state: mailingState,
    street: mailingStreet,
    street2: mailingStreet2,
    city: mailingCity,
    zip: mailingZip,
    country: mailingCountry,
    google_place_formatted_address: mailingGooglePlaceFormattedAddress,
  };

  // dob[...] needs to be changed to date_of_birth (YYYY-MM-DD)
  const date_of_birth = `${dob.year}-${dob.month}-${dob.day}`;

  // is_address_on_document is called document_address_mismatch on API
  const document_address_mismatch =
    is_address_on_document === undefined ? undefined : is_address_on_document === "no";

  // API thinks of residence permit and identity card as "ID"
  document_type = ["RP", "IC"].includes(document_type) ? "ID" : document_type;

  // Acuant wants US states as postal abbreviations
  if (permanentAddress.country === "US") {
    permanentAddress.state = toStateISO(permanentAddress.country, permanentAddress.state);
  }

  // Prep documents
  [document_front, document_back] = await prepDocuments(document_front, document_back);

  // Prep supporting documents
  extra_files = await prepSupportingDocuments(extra_files);

  const mailing_address = useForMailingAddress
    ? { ...permanentAddress }
    : {
        ...mailingAddress,
        state:
          mailingAddress.country === "US"
            ? toStateISO(mailingAddress.country, mailingAddress.state)
            : mailingAddress.state,
      };

  return {
    kyc_pii: {
      country,
      date_of_birth,
      // Now that adding Mailing Address is required, this legacy field should always be true.
      use_1099: true,
      document_type,
      document_front,
      document_back,
      document_address_mismatch,
      ...personal,
      ...taxInfo,
      ...permanentAddress,
      ...documents,
      // We will expect validation of this form (required/optional) on form level
      mailing_address,
    },
    pii_level,
    extra_files,
  };
};

export const passesExtraSSNValidation = (value) => {
  if (!value) return false;
  const cleanVal = value.replaceAll("-", "");
  const invalidLength = cleanVal.length !== 9; // this isn't really needed as it covers other validation on the form, but makes this check easier to test
  const firstThree = (cleanVal || "").slice(0, 3);
  const firstThreeInt = parseInt(firstThree, 10) || -1; // if for some reason we can't parse an int, giving some negative value to run off of
  const invalidFirst3Digits = firstThree === "000" || firstThree === "666" || firstThreeInt >= 900; // invalid values: 000, 111, 900 to 999
  const invalid4thAnd5thChars = (cleanVal || "").slice(3, 5) === "00";
  const invalidLast4 = (cleanVal || "").slice(5, 9) === "0000";
  const disallowedValues = ["111111111", "333333333", "666666666", "123456789"];
  const isValid = !(
    invalidLength ||
    invalidFirst3Digits ||
    invalid4thAnd5thChars ||
    invalidLast4 ||
    disallowedValues.includes(cleanVal)
  );
  return isValid;
};

export const RETRY_RULE_CODE_MESSAGES = {
  TEST_CODE: "This is a retry rule code message for 'TEST_CODE'",
  US_VERIFICATION_ISSUE: "Please review your name, SSN, and date of birth before submitting again",
};

export const getInitialValues = (config = { limitToBasicPII: false }) => {
  return {
    email: "",
    country: "",
    personal: {
      first: "",
      middle: "",
      last: "",
      dob: {
        day: "",
        month: "",
        year: "",
      },
    },
    taxInfo: {
      ssn: "",
      signature: "",
    },
    contact: {
      mailingAddress: {
        street: "",
        street2: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        googlePlacesAutocomplete: "",
      },
      street: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      googlePlacesAutocomplete: "",
      useForMailingAddress: false,
    },
    documents: {
      document_type: "", // DL, PP or ID
      document_front: undefined,
      document_back: undefined,
      document_country: undefined,
      is_address_on_document: undefined,
      extra_files: [],
    },
    __metadata: {
      permanentGooglePlaceFormattedAddress: undefined,
      mailingGooglePlaceFormattedAddress: undefined,
    },
    pii_level: config.limitToBasicPII ? "basic" : "full",
  };
};
